import {
  BillSortOption,
  BillStatus,
  BillType,
  enumBillSortOption,
} from '@rematter/frontend-lib-api-client';
import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import { billAccountsQueryAtom } from '../_graphql/queryBillAccounts';
import { billsQueryAtom } from '../_graphql/queryFinanceBills';
import { financeFacilitiesQueryAtom } from '../_graphql/queryFinanceFacilities';
import { BillRecord } from '../types';

import { BookingRecord } from '~/modules/trade/types/booking';
import { atomFromQueryString } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $columnSelectedStatuses =
  atomFromQueryString.stringSet<BillStatus>('colStatuses');
export const $columnSelectedTypes =
  atomFromQueryString.stringSet<BillType>('types');
export const $columnSelectedAccountIds = atomFromQueryString.stringSet('acc');
export const $columnSelectedFacilityIds = atomFromQueryString.stringSet('fac');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);
export const $dateRange = atomFromQueryString.dateRange();
export const $viewMode = atomFromQueryString.string<'flat' | 'grouped'>(
  'view',
  'flat',
);

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedKeys = atom<DataGridSelection>(new Set<React.Key>());
export const $isPaymentDrawerOpen = atom<boolean>(false);
export const $isLoadDialogOpen = atom<boolean>(false);
export const $isBillWizardOpen = atom<boolean>(false);
export const $selectedBooking = atom<BookingRecord | null>(null);
export const $billsToPay = atom<BillRecord[] | null>(null);
export const $currentSorting = atom<BillSortOption[]>([
  enumBillSortOption.DocumentDateDesc,
]);

// 🔘🔘🔘 FILTER ATOMS
export const $accountFilterSearchTerm = atom<string | undefined>(undefined);
export const $facilityFilterSearchTerm = atom<string | undefined>(undefined);

// 🟢🟢🟢 COMPUTED DATA
export const $statuses = atom((get) => {
  const selectedStatuses = get($columnSelectedStatuses);
  return Array.from(selectedStatuses);
});

// 🟢🟢🟢 FILTER REMOTE DATA
export const $remoteFilterAccounts = billAccountsQueryAtom((get) => {
  const searchTerm = get($accountFilterSearchTerm);
  return {
    variables: { searchTerm, limit: 20 },
  };
});

export const $remoteFilterFacilities = financeFacilitiesQueryAtom((get) => {
  const searchTerm = get($facilityFilterSearchTerm);
  return {
    variables: { searchTerm, limit: 20 },
  };
});

// 🟢🟢🟢 REMOTE DATA
export const $remoteBills = billsQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const selectedStatuses = get($statuses);
  const columnSelectedTypes = get($columnSelectedTypes);
  const columnSelectedAccountIds = get($columnSelectedAccountIds);
  const columnSelectedFacilityIds = get($columnSelectedFacilityIds);
  const currentSorting = get($currentSorting);
  const viewMode = get($viewMode);
  const limit = get($limit);
  const offset = get($offset);

  const sorting: BillSortOption[] =
    viewMode === 'grouped'
      ? [enumBillSortOption.AccountAsc, ...currentSorting]
      : currentSorting;

  const params: {
    searchTerm?: string;
    statuses?: BillStatus[];
    types?: BillType[];
    accountIDs?: string[];
    facilityIDs?: string[];
    limit?: number;
    offset?: number;
    sorting: BillSortOption[];
  } = {
    limit,
    offset,
    sorting,
  };

  if (searchTerm) params.searchTerm = searchTerm;
  if (selectedStatuses.length > 0) params.statuses = selectedStatuses;
  if (columnSelectedTypes.size > 0)
    params.types = Array.from(columnSelectedTypes);
  if (columnSelectedAccountIds.size > 0)
    params.accountIDs = Array.from(columnSelectedAccountIds);
  if (columnSelectedFacilityIds.size > 0)
    params.facilityIDs = Array.from(columnSelectedFacilityIds);

  return {
    variables: params,
  };
});
